import type { OutboxState } from '@jq4race/shared';
import Dexie, { type EntityTable } from 'dexie';

// Row shapes (design §5). Only the columns Dexie needs to index are typed
// strictly here — outbox/blobs get their full payload shape from slices 4/6
// (photo pipeline, outbox domain). Keeping the row type loose-but-honest now
// avoids re-declaring the schema when those slices extend it (Dexie
// versioning is additive-only, see below).
export interface OutboxRow {
  id: string; // = clientRequestId (design D1)
  state: OutboxState;
  orgId: string;
  seq: number;
  nextAttemptAt: string | null;
  [key: string]: unknown;
}

export interface BlobRow {
  id: string;
  outboxId: string;
  role?: string;
  // Typed by Slice 4 (photo pipeline, design §5 "blobs" row shape). Kept
  // optional (not required) so slice 3's own tests, which put minimal rows,
  // stay valid — Dexie does not enforce required fields at the row-type
  // level, only at the schema/index level (`id, outboxId` above).
  blob?: Blob;
  contentType?: string;
  bytes?: number;
  uploadedKey?: string;
  uploadedAt?: string;
  // pwa-offline-first Slice 6 (coordinator finding #5) — stamped by
  // DexieBlobStore.put() at capture time; used by cleanupOrphanedBlobs to
  // decide how OLD an unlinked (no outbox row) blob is.
  createdAt?: string;
  [key: string]: unknown;
}

export interface KvRow {
  key: string;
  value: unknown;
}

// jq4-offline — the single IndexedDB database for every offline concern
// (outbox queue, photo blobs, and misc key/value state incl. the offline
// profile and the persisted TanStack Query cache). Design D6.
//
// Versioning rule (design §5, "Dexie schema v1"): additive only. Never
// remove or repurpose a store/index while rows may still exist for it — add
// a new `.version(n)` block with `.stores()` (repeating unchanged stores)
// and an `.upgrade()` callback when data needs to move. Every version bump
// MUST ship its own v(n-1)→v(n) fake-indexeddb migration test alongside this
// file (see offline-db.migration.spec.ts, added starting at v2).
export class OfflineDb extends Dexie {
  outbox!: EntityTable<OutboxRow, 'id'>;
  blobs!: EntityTable<BlobRow, 'id'>;
  kv!: EntityTable<KvRow, 'key'>;

  constructor() {
    super('jq4-offline');
    this.version(1).stores({
      outbox: 'id, state, [orgId+state], seq, nextAttemptAt',
      blobs: 'id, outboxId',
      kv: 'key',
    });
  }
}

// Single shared instance — every module in apps/pwa imports this, never
// constructs its own OfflineDb (would open a second connection to the same
// underlying IndexedDB database, which Dexie supports but which is never the
// intent here). Tests construct their own instance to stay isolated (see
// offline-db.spec.ts).
export const offlineDb = new OfflineDb();
